import { HttpStatusCode } from "axios";
import IPage from "../models/page/IPage";
import networkService from "./NetworkService";
import { validate as validateGuid } from 'uuid'
import IPagePreview from "../models/page/IPagePreview";

class PageService {

    /**
     * @description Gets all pages
     */
    async getPages(): Promise<IPage[]> {
        const result = await networkService.get<IPage[]>('/pages');

        if (!result.isSuccessful) {
            throw result.data;
        }

        if (result.status === HttpStatusCode.NoContent) {
            return [];
        }

        const pages = (result.data as IPage[])
            .sort((a, b) => a.title.title.localeCompare(b.title.title));

        return pages;
    }

    /**
     * @description Gets preview info about all pages
     */
    async getPagePreviews(): Promise<IPagePreview[]> {
        const result = await networkService.get<IPagePreview[]>('/pages/previews');

        if (!result.isSuccessful) {
            throw result.data;
        }

        if (result.status === HttpStatusCode.NoContent) {
            return [];
        }

        return result.data as IPagePreview[];
    }

    /**
     * @description Gets a page by its URL slug
     * @param {string} idOrSlug The ID / URL slug of the page
     */
    async getPage(idOrSlug: string, area?: string): Promise<IPage> {
        const path = area && !validateGuid(idOrSlug)
            ? `/page/${area}/${idOrSlug}`
            : `/page/${idOrSlug}`;

        const result = await networkService.get<IPage>(path);

        if (!result.isSuccessful) {
            throw result.data;
        }

        return result.data as IPage;
    }

    /**
     * @description Creates a new page
     * @param {IPage} page The page data to be created
     */
    async createPage(page: IPage): Promise<string> {
        page.sections.forEach(section => {
            section.content = {
                "$type": section.content.type,
                ...section.content
            } as any;
        });

        const result = await networkService.put<IPage, string>(`/page`, page);
        if (result.isSuccessful) {
            return result.data as string;
        }

        throw result.data;
    }

    /**
     * @description Updates an existing page
     * @param {IPage} page The page data to be updated
     */
    async updatePage(page: IPage): Promise<boolean> {
        page.sections.forEach(section => {
            section.content = {
                "$type": section.content.type,
                ...section.content
            } as any;
        });

        const result = await networkService.patch<IPage, void>(`/page`, page);
        return result.isSuccessful;
    }
    
    /**
     * @description Deletes a page by its ID
     * @param {string} id The ID of the page to be deleted
     */
    async deletePage(id: string): Promise<boolean> {
        const result = await networkService.delete(`/page/${id}`);
        return result.isSuccessful;
    }

}

const pageService = new PageService();
export default pageService;